<div class="flex h-screen overflow-hidden bg-surface-raised">
  <!-- Sidebar -->
  <nav
    class="flex flex-col shrink-0 bg-surface-0 border-r border-surface-200 h-full overflow-hidden transition-[width] duration-200"
    [class]="collapsed() ? 'w-16' : 'w-60'"
  >
    <!-- Brand: logo (crop unchanged) + tenant badge -->
    <div class="border-b border-surface-200 px-4 pt-4 pb-3 shrink-0">
      <div class="flex items-center h-7">
        <div
          class="overflow-hidden shrink-0 transition-[width] duration-200"
          [style.width]="collapsed() ? '2rem' : '10.5rem'"
        >
          <img
            src="/assets/logo.svg"
            alt="Everplan"
            class="w-auto block"
            [class]="collapsed() ? 'h-6' : 'h-7'"
            style="max-width: none"
          >
        </div>
      </div>
      <div class="flex items-center gap-3 h-8 mt-2">
        <span
          class="flex size-6 items-center justify-center rounded-md bg-surface-200 text-surface-600 text-[11px] font-semibold shrink-0"
          >{{ tenantInitial() }}</span
        >
        @if (!collapsed()) {
          <span class="text-[13px] text-surface-500 truncate">{{ tenantName() }}</span>
        }
      </div>
    </div>

    <!-- Dev branch chip -->
    @if (branch && !collapsed()) {
      <button
        type="button"
        class="flex shrink-0 cursor-pointer items-center gap-1.5 mx-2 mt-2 rounded-full border border-surface-200 bg-surface-100 px-2.5 py-1 text-xs font-medium text-surface-600 hover:text-surface-900 max-w-[calc(100%-1rem)]"
        [pTooltip]="'Development build · ' + branch + ' — click to copy'"
        tooltipPosition="bottom"
        (click)="copyBranch()"
      >
        <span class="material-symbols-outlined text-[16px] shrink-0 text-primary-600">account_tree</span>
        <span class="truncate">{{ branch }}</span>
      </button>
    }

    <!-- Reusable nav link (icon rail + label rail; lock badge + active state) -->
    <ng-template #navLink let-item>
      <a
        [routerLink]="item.route"
        routerLinkActive
        #rl="routerLinkActive"
        class="flex items-center gap-3 h-9 px-3 rounded-lg text-sm transition-colors overflow-hidden shrink-0"
        [class]="rl.isActive ? 'bg-primary-50 text-primary-700 font-medium' : 'text-surface-600 hover:bg-surface-100 hover:text-surface-900'"
        [pTooltip]="item.labelKey | translate"
        [tooltipDisabled]="!collapsed()"
        tooltipPosition="right"
      >
        <span class="relative shrink-0 leading-none">
          <span class="material-symbols-outlined text-[20px]">{{ item.icon }}</span>
          @if (item.locked) {
            <app-lock-badge tooltipPosition="right" />
          }
        </span>
        @if (!collapsed()) {
          <span class="flex-1 truncate">{{ item.labelKey | translate }}</span>
        }
      </a>
    </ng-template>

    <!-- Main nav: Dashboard alone, then grouped by domain -->
    <div class="flex flex-col flex-1 gap-0.5 p-2 overflow-y-auto">
      <ng-container [ngTemplateOutlet]="navLink" [ngTemplateOutletContext]="{ $implicit: dashboardItem() }" />

      @for (group of navGroups(); track group.labelKey) {
        <!-- Group header: label when expanded, a fixed-height divider when
             collapsed — same height in both states so items never shift. -->
        @if (!collapsed()) {
          <div class="h-8 px-3 flex items-end pb-1 text-[11px] font-medium uppercase tracking-wide text-surface-400">
            {{ group.labelKey | translate }}
          </div>
        } @else {
          <div class="h-8 px-3 flex items-center"><span class="w-full border-t border-surface-200"></span></div>
        }
        @for (item of group.items; track item.route) {
          <ng-container [ngTemplateOutlet]="navLink" [ngTemplateOutletContext]="{ $implicit: item }" />
        }
      }
    </div>

    <!-- Bottom: settings · user chip · collapse -->
    <div class="flex flex-col gap-0.5 p-2 border-t border-surface-200">
      <ng-container [ngTemplateOutlet]="navLink" [ngTemplateOutletContext]="{ $implicit: settingsItem() }" />

      <!-- User chip → profile: avatar on the icon rail, name + role on the label rail -->
      <a
        routerLink="/profile"
        class="flex items-center gap-3 h-9 px-3 rounded-lg hover:bg-surface-100 transition-colors shrink-0"
        [pTooltip]="userName()"
        [tooltipDisabled]="!collapsed()"
        tooltipPosition="right"
      >
        <span class="flex size-6 items-center justify-center rounded-full bg-primary-500 text-white text-[10px] font-medium shrink-0">
          {{ userInitials() }}
        </span>
        @if (!collapsed()) {
          <span class="flex flex-col leading-tight min-w-0">
            <span class="text-[13px] text-surface-900 truncate">{{ userName() }}</span>
            @if (roleKey()) {
              <span class="text-[11px] text-surface-400 truncate">{{ 'profile.roles.' + roleKey() | translate }}</span>
            }
          </span>
        }
      </a>

      <button
        type="button"
        class="flex items-center gap-3 h-9 px-3 rounded-lg text-sm text-surface-600 hover:bg-surface-100 hover:text-surface-900 transition-colors w-full"
        (click)="collapsed.set(!collapsed())"
        [pTooltip]="(collapsed() ? 'shell.nav.expand' : 'shell.nav.collapse') | translate"
        [tooltipDisabled]="!collapsed()"
        tooltipPosition="right"
      >
        <span class="material-symbols-outlined text-[20px] shrink-0">
          {{ collapsed() ? 'chevron_right' : 'chevron_left' }}
        </span>
        @if (!collapsed()) {
          <span>{{ 'shell.nav.collapse' | translate }}</span>
        }
      </button>
    </div>
  </nav>

  <!-- Main content -->
  <main class="flex flex-col flex-1 overflow-y-auto *:grow *:overflow-hidden">
    <router-outlet />
  </main>
</div>

<p-toast />
<p-confirmdialog />
